import {useEffect, useRef} from "react";

export const ChromaKeyVideoPlayer = ({
  videoRef,
  isConnected,
  enableChromaKey = true,
  wrapperClassName = "",
  playerClassName = "",
  videoId = undefined,
  videoProps = {},
  videoStyle = {},
  children = null,
}) => {
  const canvasRef = useRef(null);
  const chromaRafRef = useRef(null);

  useEffect(() => {
    if (!enableChromaKey) {
      return;
    }

    const videoEl = videoRef?.current;
    const canvasEl = canvasRef.current;

    if (!isConnected || !videoEl || !canvasEl) {
      if (chromaRafRef.current) {
        cancelAnimationFrame(chromaRafRef.current);
        chromaRafRef.current = null;
      }
      return;
    }

    const ctx = canvasEl.getContext("2d", {willReadFrequently: true});
    if (!ctx) return;

    let isActive = true;

    const renderFrame = () => {
      if (!isActive) return;

      if (
        videoEl.readyState >= 2 &&
        videoEl.videoWidth > 0 &&
        videoEl.videoHeight > 0
      ) {
        const dpr = window.devicePixelRatio || 1;
        const displayWidth = Math.max(1, Math.round(canvasEl.clientWidth || 1));
        const displayHeight = Math.max(
          1,
          Math.round(canvasEl.clientHeight || 1),
        );
        const targetWidth = Math.round(displayWidth * dpr);
        const targetHeight = Math.round(displayHeight * dpr);

        if (
          canvasEl.width !== targetWidth ||
          canvasEl.height !== targetHeight
        ) {
          canvasEl.width = targetWidth;
          canvasEl.height = targetHeight;
        }

        const sourceWidth = videoEl.videoWidth;
        const sourceHeight = videoEl.videoHeight;
        const scale = Math.min(
          targetWidth / sourceWidth,
          targetHeight / sourceHeight,
        );
        const drawWidth = Math.max(1, Math.round(sourceWidth * scale));
        const drawHeight = Math.max(1, Math.round(sourceHeight * scale));
        const drawX = Math.round((targetWidth - drawWidth) / 2);
        const drawY = Math.round(targetHeight - drawHeight);

        ctx.clearRect(0, 0, targetWidth, targetHeight);
        ctx.drawImage(videoEl, drawX, drawY, drawWidth, drawHeight);

        const frame = ctx.getImageData(drawX, drawY, drawWidth, drawHeight);
        const {data} = frame;

        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];

          const isGreen =
            g > 96 && g > r * 1.2 && g > b * 1.15 && g - Math.max(r, b) > 18;

          if (isGreen) {
            const alphaFactor = Math.min(1, (g - Math.max(r, b)) / 120);
            data[i + 3] = Math.max(
              0,
              Math.round(data[i + 3] * (1 - alphaFactor)),
            );
          }
        }

        ctx.putImageData(frame, drawX, drawY);
      }

      chromaRafRef.current = requestAnimationFrame(renderFrame);
    };

    renderFrame();

    return () => {
      isActive = false;
      if (chromaRafRef.current) {
        cancelAnimationFrame(chromaRafRef.current);
        chromaRafRef.current = null;
      }
    };
  }, [enableChromaKey, isConnected, videoRef]);

  return (
    <div className={wrapperClassName}>
      <div className={playerClassName}>
        {enableChromaKey && (
          <canvas
            ref={canvasRef}
            style={{
              width: "100%",
              height: "100%",
              display: "block",
              pointerEvents: "none",
            }}
          />
        )}
        <video
          id={videoId}
          ref={videoRef}
          {...videoProps}
          style={{
            ...(enableChromaKey
              ? {
                  position: "absolute",
                  inset: 0,
                  opacity: 0,
                  pointerEvents: "none",
                }
              : {}),
            ...videoStyle,
          }}
        >
          {children}
        </video>
      </div>
    </div>
  );
};
